/**
 * TableToolbar - Adds Export CSV and Column Toggle to any existing table
 * Uses CSS-based column hiding via a style tag - no changes needed to table markup
 */
import React, { useState, useEffect, useId } from 'react';
import { Button } from '../ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuCheckboxItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Download, Columns3 } from 'lucide-react';
import { exportToCSV } from '../../utils/exportCSV';

const PREFS_KEY = 'table_cols_';

const TableToolbar = ({
  tableId,
  columns,    // Array of { key, label, accessor? }
  data,       // Array of row data for export
  filename = 'export',
  children,
  inline = false,
  showColumns = true,
  defaultHidden = [],  // column keys hidden by default (unless user has saved prefs)
}) => {
  const [hiddenCols, setHiddenCols] = useState(new Set());
  const styleId = useId();

  useEffect(() => {
    if (!tableId) return;
    const saved = localStorage.getItem(PREFS_KEY + tableId);
    if (saved) {
      try {
        setHiddenCols(new Set(JSON.parse(saved)));
      } catch { /* ignore */ }
    } else if (defaultHidden.length > 0) {
      // Apply defaults only when no saved preference exists
      setHiddenCols(new Set(defaultHidden));
    }
  }, [tableId]);

  // Inject CSS to hide columns by index in the nearest table
  useEffect(() => {
    let styleEl = document.getElementById(`table-style-${tableId}`);
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = `table-style-${tableId}`;
      document.head.appendChild(styleEl);
    }

    const hiddenIndices = columns.reduce((acc, col, idx) => {
      if (hiddenCols.has(col.key)) acc.push(idx + 1); // nth-child is 1-based
      return acc;
    }, []);

    if (hiddenIndices.length > 0) {
      const selectors = hiddenIndices.map(i => 
        `[data-table-id="${tableId}"] th:nth-child(${i}), [data-table-id="${tableId}"] td:nth-child(${i})`
      ).join(', ');
      styleEl.textContent = `${selectors} { display: none !important; }`;
    } else {
      styleEl.textContent = '';
    }

    return () => {
      if (styleEl && styleEl.parentNode) styleEl.parentNode.removeChild(styleEl);
    };
  }, [hiddenCols, columns, tableId]);

  const toggleColumn = (key) => {
    const newHidden = new Set(hiddenCols);
    if (newHidden.has(key)) newHidden.delete(key);
    else newHidden.add(key);
    setHiddenCols(newHidden);
    localStorage.setItem(PREFS_KEY + tableId, JSON.stringify([...newHidden]));
  };

  const handleExport = () => {
    const visible = columns.filter(c => !hiddenCols.has(c.key));
    exportToCSV(data, visible, filename);
  };

  return (
    <div className={inline ? 'flex items-center gap-2 ml-auto' : 'flex items-center gap-2 justify-end -mb-4'}>
      {children}
      {showColumns && (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-7 text-xs">
            <Columns3 className="w-3 h-3 mr-1" /> Columns
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="max-h-64 overflow-y-auto">
          {columns.map(col => (
            <DropdownMenuCheckboxItem
              key={col.key}
              checked={!hiddenCols.has(col.key)}
              onCheckedChange={() => toggleColumn(col.key)}
            >
              {col.label}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      )}
      <Button variant="outline" size="sm" className="h-7 text-xs" onClick={handleExport}>
        <Download className="w-3 h-3 mr-1" /> Export CSV
      </Button>
      {showColumns && hiddenCols.size > 0 && (
        <Button variant="ghost" size="sm" className="h-7 text-[10px] text-muted-foreground" onClick={() => { setHiddenCols(new Set()); localStorage.removeItem(PREFS_KEY + tableId); }}>
          Reset
        </Button>
      )}
    </div>
  );
};

export { TableToolbar };
export default TableToolbar;
